import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// Cache for redirect lookups to avoid repeated API calls
const redirectCache = new Map<string, { target_path: string; status_code: number } | null>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const cacheTimestamps = new Map<string, number>();

export const useRedirectMiddleware = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isCheckingRef = useRef(false);
  
  useEffect(() => {
    // Skip if already checking or on admin routes
    if (isCheckingRef.current || location.pathname.startsWith('/admin')) {
      return;
    }

    const checkRedirect = async () => {
      try {
        isCheckingRef.current = true;
        const currentPath = location.pathname;
        
        // Check cache first
        const cached = redirectCache.get(currentPath);
        const cacheTime = cacheTimestamps.get(currentPath);
        const now = Date.now();
        
        if (cached && cacheTime && (now - cacheTime) < CACHE_DURATION) {
          if (cached.target_path) {
            // Immediate redirect from cache
            navigate(cached.target_path, { replace: true });
            return;
          }
          // Cached "no redirect" - skip API call
          return;
        }

        // Fast API call with minimal payload and timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 1000); // 1 second timeout
        
        try {
          const response = await fetch(
            `/api/handle-redirect`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ path: currentPath }),
              signal: controller.signal
            }
          );

          clearTimeout(timeoutId);

          if (!response.ok) {
            // Cache "no redirect" to avoid future calls
            redirectCache.set(currentPath, null);
            cacheTimestamps.set(currentPath, now);
            return;
          }

          const data = await response.json();
          
          if (data && data.target_path && data.found !== false) {
            // Cache the redirect
            redirectCache.set(currentPath, {
              target_path: data.target_path,
              status_code: data.status_code || 301
            });
            cacheTimestamps.set(currentPath, now);
            
            // Immediate redirect
            navigate(data.target_path, { replace: true });
          } else {
            // Cache "no redirect"
            redirectCache.set(currentPath, null);
            cacheTimestamps.set(currentPath, now);
          }
        } catch (error) {
          clearTimeout(timeoutId);
          if (error.name === 'AbortError') {
            console.warn('Redirect check timed out for:', currentPath);
          } else {
            console.error('Error in redirect check:', error);
          }
          // Cache "no redirect" on error to avoid repeated failures
          redirectCache.set(currentPath, null);
          cacheTimestamps.set(currentPath, now);
        }
      } catch (error) {
        console.error('Error in redirect middleware:', error);
      } finally {
        isCheckingRef.current = false;
      }
    };

    // Use requestIdleCallback for better performance, fallback to immediate
    if ('requestIdleCallback' in window) {
      requestIdleCallback(checkRedirect, { timeout: 100 });
    } else {
      // Small delay to allow page to start rendering
      setTimeout(checkRedirect, 10);
    }
  }, [location.pathname, navigate]);
};

// Function to preload redirects for common paths
export const preloadRedirects = async (paths: string[]) => {
  const uncachedPaths = paths.filter(path => !redirectCache.has(path));
  
  if (uncachedPaths.length === 0) return;

  try {
    // Batch request for multiple paths
    const response = await fetch(
      `/api/handle-redirect`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ paths: uncachedPaths })
      }
    );

    if (response.ok) {
      const data = await response.json();
      const now = Date.now();
      
      // Cache all results
      uncachedPaths.forEach((path, index) => {
        const result = data.results?.[index];
        if (result && result.target_path) {
          redirectCache.set(path, {
            target_path: result.target_path,
            status_code: result.status_code || 301
          });
        } else {
          redirectCache.set(path, null);
        }
        cacheTimestamps.set(path, now);
      });
    }
  } catch (error) {
    console.error('Error preloading redirects:', error);
  }
};

// Clear cache function for admin use
export const clearRedirectCache = () => {
  redirectCache.clear();
  cacheTimestamps.clear();
};
